import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Minus, Trash2, Check, ShoppingCart, UtensilsCrossed, BarChart3, ArrowRight, ChefHat, Users, Clock, Trophy, Lock, Star, Sparkles, Loader2, Play, X, ChevronLeft, ChevronRight, Search, Pencil, Bell } from "lucide-react";
import { BrutalButton, BrutalCard, SectionTitle, EmptyState, Pill } from "@/components/Brutal";
import { GroupMultiSelect, RecipeCombobox } from "@/components/Selects";
import { PhotoInput, PhotosInput } from "@/components/AiComponents";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { useModuleGuard } from "@/lib/useModuleGuard";
import { Tip } from "@/components/Tip";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmProvider";
import { stepQuantity } from "@/lib/labels";

const RECIPE_CATEGORIES = ["Hoofdgerecht", "Voorgerecht", "Nagerecht", "Dranken", "Snack", "Overig"];
const ABSENT = "__afwezig__";
const DONTCARE = "__maakt_niet_uit__";
const SHOP_CATEGORIES = ["Groente & Fruit", "Zuivel", "Vlees & Vis", "Brood & Bakkerij", "Dranken", "Diepvries", "Kruidenier", "Overig"];
const backend = process.env.REACT_APP_BACKEND_URL;
const recipeImg = (u) => (u ? (u.startsWith("http") ? u : `${backend}${u}`) : null);
const avgRating = (r) => {
  const vals = Object.values(r?.ratings || {});
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
};

export default function Eten() {
  useModuleGuard("eten");
  const tr = useT();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "poll");
  const autoNew = params.get("new") === "1";
  const tabs = [{ id: "poll", label: "Wat eten we?", icon: BarChart3 }, { id: "recepten", label: "Recepten", icon: ChefHat }, { id: "boodschappen", label: "Boodschappen", icon: ShoppingCart }];
  return (
    <div className="space-y-5" data-testid="eten-page">
      <SectionTitle sub={tr("Poll, recepten en boodschappen")}>{tr("Eten")}</SectionTitle>
      <div className="flex gap-1.5 sm:gap-2">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`eten-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex-1 min-w-0 brutal-sm brutal-press rounded-full px-2 sm:px-4 py-2 text-xs sm:text-sm font-bold whitespace-nowrap flex items-center justify-center gap-1.5 ${tab === t.id ? "bg-brand-ink text-white" : "bg-white"}`}>
            <t.icon className="w-4 h-4 shrink-0" strokeWidth={2.5} /><span className="truncate">{tr(t.label)}</span>
          </button>
        ))}
      </div>
      {tab === "poll" && <Polls user={user} autoNew={autoNew} algemeen={params.get("algemeen") === "1"} />}
      {tab === "recepten" && <Recepten />}
      {tab === "boodschappen" && <Boodschappen />}
    </div>
  );
}

function Polls({ user, autoNew, algemeen }) {
  const confirm = useConfirm();
  const tr = useT();
  const [polls, setPolls] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [foodMode, setFoodMode] = useState(true);
  const [question, setQuestion] = useState("Wat eten we vandaag?");
  const [options, setOptions] = useState([{ text: "", recipeId: "", image: "" }, { text: "", recipeId: "", image: "" }]);
  const [closesAt, setClosesAt] = useState("");
  const [pollPhotos, setPollPhotos] = useState([]);
  const [savingPoll, setSavingPoll] = useState(false);
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [detailRecipe, setDetailRecipe] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [pollView, setPollView] = useState("open");
  const [taskPoll, setTaskPoll] = useState(null);
  const [voterPoll, setVoterPoll] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);
  const [taskOpts, setTaskOpts] = useState({ addList: true, cook: true, cookAssignee: "", shoppingTask: false, shoppingAssignee: "", noShow: 0, extraShow: 0 });

  const load = async () => {
    setPolls((await api.get("/polls")).data);
    setGroups((await api.get("/groups")).data);
    setRecipes((await api.get("/recipes")).data);
    setUsers((await api.get("/users")).data);
    setSuggestions((await api.get("/polls/option-suggestions")).data.suggestions || []);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (autoNew) openNew(!algemeen); }, [autoNew]);

  const openNew = (food = true) => {
    setEditId(null); setFoodMode(food);
    setQuestion(food ? "Wat eten we vandaag?" : "");
    setOptions([{ text: "", recipeId: "", image: "" }, { text: "", recipeId: "", image: "" }]);
    setGroupIds([]); setClosesAt(""); setPollPhotos([]); setOpen(true);
  };

  const startEdit = (p) => {
    setEditId(p.id); setFoodMode(p.kind !== "algemeen");
    setQuestion(p.question);
    const rids = p.option_recipe_ids || [];
    const imgs = p.option_images || [];
    setOptions(p.options.map((t, i) => ({ text: t, recipeId: rids[i] || "", image: imgs[i] || "" })));
    setGroupIds(p.group_ids || []);
    setClosesAt(p.closes_at ? new Date(p.closes_at).toISOString().slice(0, 16) : "");
    setPollPhotos(p.photos || (p.image_url ? [p.image_url] : []));
    setOpen(true);
  };

  const vote = (id, option) => api.post(`/polls/${id}/vote`, { option }).then(load).catch((e) => toast.error(e.response?.data?.detail || tr("Mislukt")));

  const remind = async (p) => {
    try {
      const { data } = await api.post(`/polls/${p.id}/remind`);
      toast.success(data.reminded > 0 ? `${tr("Herinnering verstuurd naar")} ${data.reminded}` : tr("Iedereen heeft al gestemd 🎉"));
    } catch (e) { toast.error(e.response?.data?.detail || tr("Mislukt")); }
  };

  const closeAndAsk = async (p) => {
    await api.post(`/polls/${p.id}/close`);
    await load();
    const participants = Object.values(p.votes || {}).filter((v) => v !== ABSENT).length;
    setTaskOpts({ addList: true, cook: true, cookAssignee: "", shoppingTask: false, shoppingAssignee: "", noShow: 0, extraShow: 0 });
    setTaskPoll({ ...p, _participants: participants });
  };

  const finalServings = taskPoll ? Math.max(1, (taskPoll._participants || 0) - Number(taskOpts.noShow || 0) + Number(taskOpts.extraShow || 0)) : 1;

  const createTasks = async () => {
    if (!taskOpts.addList && !taskOpts.cook && !taskOpts.shoppingTask) return toast.error(tr("Kies minstens één actie"));
    const { data } = await api.post(`/polls/${taskPoll.id}/create-tasks`, {
      add_to_list: taskOpts.addList, cook: taskOpts.cook, cook_assignee: taskOpts.cookAssignee || null,
      shopping_task: taskOpts.shoppingTask, shopping_assignee: taskOpts.shoppingAssignee || null,
      servings: finalServings,
    });
    toast.success(`Klaar voor ${data.winner} (${finalServings} pers.)`);
    setTaskPoll(null);
  };

  const setOpt = (i, key, val) => setOptions((o) => o.map((row, j) => {
    if (j !== i) return row;
    const next = { ...row, [key]: val };
    if (key === "recipeId" && val && !row.text.trim()) {
      const r = recipes.find((x) => x.id === val);
      if (r) next.text = r.name;
    }
    return next;
  }));

  const save = async () => {
    if (savingPoll) return;
    const clean = options.map((o) => ({ text: o.text.trim(), recipeId: foodMode ? o.recipeId : "", image: foodMode ? o.image : "" })).filter((o) => o.text);
    if (!question.trim() || clean.length < 2) return toast.error(tr("Vraag + min. 2 opties"));
    const payload = {
      question: question.trim(),
      options: clean.map((o) => o.text),
      option_recipe_ids: clean.map((o) => o.recipeId || null),
      option_images: clean.map((o) => o.image || null),
      group_ids: groupIds,
      closes_at: closesAt ? new Date(closesAt).toISOString() : null,
      kind: foodMode ? "eten" : "algemeen",
      image_url: pollPhotos[0] || null,
      photos: pollPhotos,
    };
    setSavingPoll(true);
    try {
      if (editId) await api.put(`/polls/${editId}`, payload);
      else await api.post("/polls", payload);
      setOpen(false); setEditId(null); setOptions([{ text: "", recipeId: "", image: "" }, { text: "", recipeId: "", image: "" }]);
      setQuestion("Wat eten we vandaag?"); setGroupIds([]); setClosesAt(""); setPollPhotos([]);
      load(); toast.success(editId ? tr("Poll bijgewerkt!") : tr("Poll geplaatst!"));
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Opslaan mislukt, probeer opnieuw"));
    } finally {
      setSavingPoll(false);
    }
  };

  const isClosed = (p) => p.closed || (p.closes_at && new Date(p.closes_at) < new Date());
  const openRecipe = (rid) => { const r = recipes.find((x) => x.id === rid); if (r) setDetailRecipe(r); else toast.error(tr("Recept niet gevonden")); };

  return (
    <div className="space-y-4">
      <BrutalButton data-testid="new-poll-btn" className="w-full" onClick={() => openNew(true)}><Plus className="w-5 h-5" strokeWidth={3} />{tr("Nieuwe poll")}</BrutalButton>
      <div className="flex gap-2">
        <button data-testid="pollview-open" onClick={() => setPollView("open")}
          className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${pollView === "open" ? "bg-brand-ink text-white" : "bg-white"}`}>{tr("Open")} ({polls.filter((p) => !isClosed(p)).length})</button>
        <button data-testid="pollview-gesloten" onClick={() => setPollView("gesloten")}
          className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${pollView === "gesloten" ? "bg-brand-ink text-white" : "bg-white"}`}>{tr("Gesloten")} ({polls.filter((p) => isClosed(p)).length})</button>
      </div>
      {polls.filter((p) => pollView === "open" ? !isClosed(p) : isClosed(p)).length === 0 && <EmptyState icon={UtensilsCrossed} title={pollView === "open" ? tr("Nog geen open polls") : tr("Geen gesloten polls")} text={tr("Vraag de groep wat er gegeten wordt.")} />}
      {polls.filter((p) => pollView === "open" ? !isClosed(p) : isClosed(p)).map((p) => {
        const votes = p.votes || {};
        const total = Object.values(votes).filter((v) => p.options.includes(v)).length || 1;
        const counts = p.options.map((o) => Object.values(votes).filter((v) => v === o).length);
        const absentCount = Object.values(votes).filter((v) => v === ABSENT).length;
        const dontcareCount = Object.values(votes).filter((v) => v === DONTCARE).length;
        const participants = Object.values(votes).filter((v) => v !== ABSENT).length;
        const maxC = Math.max(0, ...counts);
        const closed = isClosed(p);
        const winnerIdx = closed && maxC > 0 ? counts.indexOf(maxC) : -1;
        const recipeIds = p.option_recipe_ids || [];
        const myVote = votes[user.user_id];
        return (
          <BrutalCard key={p.id} data-testid={`poll-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-head font-bold text-lg">{p.question}</h3>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {closed ? <Pill className="bg-brand-ink text-white"><Lock className="w-3 h-3" strokeWidth={2.5} />{tr("Gesloten")}</Pill>
                    : p.closes_at ? <Pill className="bg-brand-yellow"><Clock className="w-3 h-3" strokeWidth={2.5} />{tr("Sluit")} {new Date(p.closes_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</Pill>
                    : <Pill className="bg-brand-teal/20">{tr("Open")}</Pill>}
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                {!closed && <button data-testid={`edit-poll-${p.id}`} onClick={() => startEdit(p)} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><Pencil className="w-4 h-4" strokeWidth={2.5} /></button>}
                <button data-testid={`delete-poll-${p.id}`} onClick={async () => { if (await confirm({ title: "Poll verwijderen?", description: "De poll en alle stemmen worden verwijderd." })) api.delete(`/polls/${p.id}`).then(load); }} className="brutal-sm brutal-press rounded-full p-1.5 bg-white"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
              </div>
            </div>

            {(() => { const gal = (p.photos && p.photos.length) ? p.photos : (p.image_url ? [p.image_url] : []); const src = (u) => u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
              if (gal.length === 0) return null;
              if (gal.length === 1) return <img src={src(gal[0])} alt="" className="w-full max-h-56 object-cover brutal-sm rounded-xl mt-3" data-testid={`poll-img-${p.id}`} />;
              return <div className="grid grid-cols-2 gap-2 mt-3" data-testid={`poll-gallery-${p.id}`}>{gal.slice(0, 4).map((u, i) => (<div key={i} className="relative"><img src={src(u)} alt="" className="w-full h-32 object-cover brutal-sm rounded-xl" data-testid={`poll-img-${p.id}-${i}`} />{i === 3 && gal.length > 4 && <div className="absolute inset-0 bg-black/50 rounded-xl flex items-center justify-center text-white font-head font-black">+{gal.length - 4}</div>}</div>))}</div>;
            })()}
            <div className="space-y-2 mt-3">
              {p.options.map((o, i) => {
                const pct = Math.round((counts[i] / total) * 100);
                const isWinner = i === winnerIdx;
                const rid = recipeIds[i];
                const optImgs = p.option_images || [];
                const rimg = optImgs[i] ? recipeImg(optImgs[i]) : (rid ? recipeImg(recipes.find((x) => x.id === rid)?.image_url) : null);
                return (
                  <button key={o} data-testid={`vote-${p.id}-${i}`} disabled={closed}
                    onClick={() => api.post(`/polls/${p.id}/vote`, { option: o }).then(load).catch((e) => toast.error(e.response?.data?.detail || tr("Mislukt")))}
                    className={`w-full relative brutal-sm rounded-xl overflow-hidden text-left ${isWinner ? "border-brand-violet border-2" : myVote === o ? "border-brand-violet" : ""} ${closed ? "cursor-default" : ""}`}>
                    <div className={`absolute inset-y-0 left-0 ${isWinner ? "bg-brand-violet/30" : "bg-brand-green/40"}`} style={{ width: `${pct}%` }} />
                    <div className="relative px-3 py-2.5 flex items-center justify-between font-bold">
                      <span className="flex items-center gap-2 min-w-0">
                        {rimg ? <img src={rimg} alt="" className="w-9 h-9 object-cover rounded-lg brutal-sm shrink-0" /> : null}
                        {isWinner && <Trophy className="w-4 h-4 text-brand-violet shrink-0" strokeWidth={2.5} />}
                        {!isWinner && myVote === o && <Check className="w-4 h-4 shrink-0" strokeWidth={3} />}
                        {rid && !rimg && <ChefHat className="w-4 h-4 text-brand-ink/50 shrink-0" strokeWidth={2.5} />}
                        <span className="truncate">{o}</span>
                      </span>
                      <span className="text-sm shrink-0 ml-2">{counts[i]} · {pct}%</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2 mt-2">
              <button data-testid={`vote-absent-${p.id}`} disabled={closed} onClick={() => vote(p.id, ABSENT)}
                className={`flex-1 brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold ${myVote === ABSENT ? "bg-red-500 text-white" : "bg-white"} ${closed ? "cursor-default" : ""}`}>
                🚫 {tr("Afwezig")} {absentCount > 0 && `(${absentCount})`}
              </button>
              <button data-testid={`vote-dontcare-${p.id}`} disabled={closed} onClick={() => vote(p.id, DONTCARE)}
                className={`flex-1 brutal-sm brutal-press rounded-xl px-3 py-2 text-sm font-bold ${myVote === DONTCARE ? "bg-brand-blue text-white" : "bg-white"} ${closed ? "cursor-default" : ""}`}>
                🤷 {tr("Maakt niet uit")} {dontcareCount > 0 && `(${dontcareCount})`}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button data-testid={`poll-voters-${p.id}`} onClick={() => setVoterPoll(p)} disabled={Object.keys(votes).length === 0}
                className="text-xs font-semibold text-brand-ink/50 flex-1 text-left flex items-center gap-1 disabled:opacity-100 enabled:underline decoration-dotted underline-offset-2 enabled:hover:text-brand-violet">
                <Users className="w-3.5 h-3.5" strokeWidth={2.5} />{Object.keys(votes).length} {tr("stemmen")} · {participants} {tr("eten mee")}
              </button>
              {!closed && <BrutalButton data-testid={`remind-poll-${p.id}`} variant="white" size="sm" onClick={() => remind(p)}><Bell className="w-4 h-4" strokeWidth={2.5} />{tr("Herinneren")}</BrutalButton>}
              {!closed && <BrutalButton data-testid={`close-poll-${p.id}`} variant="white" size="sm" onClick={() => closeAndAsk(p)}><Lock className="w-4 h-4" strokeWidth={2.5} />{tr("Sluit poll")}</BrutalButton>}
              {winnerIdx >= 0 && recipeIds[winnerIdx] && (
                <BrutalButton data-testid={`open-recipe-${p.id}`} variant="green" size="sm" onClick={() => openRecipe(recipeIds[winnerIdx])}>
                  <ChefHat className="w-4 h-4" strokeWidth={2.5} />{tr("Open recept")} <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                </BrutalButton>
              )}
            </div>
            {winnerIdx >= 0 && (
              <p data-testid={`winner-${p.id}`} className="mt-2 text-sm font-bold text-brand-violet flex items-center gap-1.5"><Trophy className="w-4 h-4" strokeWidth={2.5} />{tr("Winnaar:")} {p.options[winnerIdx]}{recipeIds[winnerIdx] ? "" : ` ${tr("(geen recept gekoppeld)")}`}</p>
            )}
          </BrutalCard>
        );
      })}

      <RecipeDetail recipe={detailRecipe} onClose={() => setDetailRecipe(null)} onChanged={() => {}} />

      <Dialog open={!!voterPoll} onOpenChange={(v) => !v && setVoterPoll(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[85vh] overflow-auto" data-testid="poll-voters-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{voterPoll?.question}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Overzicht van wie op welke optie stemde.</DialogDescription>
          {voterPoll && (() => {
            const vs = voterPoll.votes || {};
            const person = (uid) => users.find((u) => u.user_id === uid);
            const votersFor = (pred) => Object.entries(vs).filter(([, v]) => pred(v)).map(([uid]) => person(uid)).filter(Boolean);
            const Chip = ({ u }) => (
              <span className="flex items-center gap-1.5 brutal-sm rounded-full bg-brand-cream pl-1 pr-3 py-1">
                {u.avatar_url ? <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" /> : <span className="w-6 h-6 rounded-full bg-brand-violet text-white flex items-center justify-center text-xs font-head font-black">{(u.name || "?").charAt(0)}</span>}
                <span className="font-bold text-sm">{u.name}</span>
              </span>
            );
            const absent = votersFor((v) => v === ABSENT);
            const dontcare = votersFor((v) => v === DONTCARE);
            return (
              <div className="space-y-3">
                {voterPoll.options.map((o, i) => {
                  const list = votersFor((v) => v === o);
                  return (
                    <div key={o} className="brutal-sm rounded-xl bg-white p-3" data-testid={`voters-option-${i}`}>
                      <p className="font-head font-bold flex items-center justify-between"><span>{o}</span><span className="text-brand-ink/40">{list.length}</span></p>
                      {list.length > 0 ? <div className="flex flex-wrap gap-2 mt-2">{list.map((u) => <Chip key={u.user_id} u={u} />)}</div>
                        : <p className="text-sm text-brand-ink/40 font-semibold mt-1">{tr("Nog niemand")}</p>}
                    </div>
                  );
                })}
                {(absent.length > 0 || dontcare.length > 0) && (
                  <div className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-2">
                    {absent.length > 0 && <div><p className="text-sm font-bold mb-1">🚫 {tr("Afwezig")}</p><div className="flex flex-wrap gap-2">{absent.map((u) => <Chip key={u.user_id} u={u} />)}</div></div>}
                    {dontcare.length > 0 && <div><p className="text-sm font-bold mb-1">🤷 {tr("Maakt niet uit")}</p><div className="flex flex-wrap gap-2">{dontcare.map((u) => <Chip key={u.user_id} u={u} />)}</div></div>}
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!taskPoll} onOpenChange={(v) => !v && setTaskPoll(null)}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto" data-testid="poll-tasks-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{tr("Poll gesloten 🎉")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Maak automatisch taken aan voor het gekozen gerecht.</DialogDescription>
          <p className="text-sm font-medium">{tr("Wil je automatisch taken aanmaken en toewijzen voor het gekozen gerecht?")}</p>

          <div className="brutal-sm rounded-xl bg-brand-violet/10 p-3">
            <p className="font-bold text-sm flex items-center gap-1.5"><Users className="w-4 h-4" strokeWidth={2.5} />{taskPoll?._participants || 0} {tr("eten mee")}</p>
            <p className="text-xs text-brand-ink/50 mb-2">{tr("\"Afwezig\" telt niet mee. Verreken no-shows en extra gasten:")}</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold">{tr("No-shows −")}</label>
                <input data-testid="poll-noshow" type="number" min="0" value={taskOpts.noShow} onChange={(e) => setTaskOpts({ ...taskOpts, noShow: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
              </div>
              <div>
                <label className="text-xs font-bold">{tr("Extra gasten +")}</label>
                <input data-testid="poll-extrashow" type="number" min="0" value={taskOpts.extraShow} onChange={(e) => setTaskOpts({ ...taskOpts, extraShow: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2 font-medium outline-none text-center" />
              </div>
            </div>
            <p className="text-sm font-bold mt-2" data-testid="poll-final-servings">{tr("Ingrediënten voor")} <span className="text-brand-violet">{finalServings} {tr("personen")}</span></p>
          </div>

          <button data-testid="poll-toggle-list" onClick={() => setTaskOpts({ ...taskOpts, addList: !taskOpts.addList })} className="w-full flex items-center justify-between font-bold text-sm brutal-sm rounded-xl bg-white px-4 py-3">
            <span className="flex items-center gap-1.5"><ShoppingCart className="w-4 h-4" strokeWidth={2.5} />{tr("Ingrediënten op boodschappenlijst")}</span>
            <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center ${taskOpts.addList ? "bg-brand-green" : "bg-white"}`}>{taskOpts.addList && <Check className="w-4 h-4" strokeWidth={3} />}</span>
          </button>

          <div className="brutal-sm rounded-xl bg-white px-4 py-3">
            <button data-testid="poll-toggle-cook" onClick={() => setTaskOpts({ ...taskOpts, cook: !taskOpts.cook })} className="w-full flex items-center justify-between font-bold text-sm">
              <span className="flex items-center gap-1.5"><ChefHat className="w-4 h-4" strokeWidth={2.5} />{tr("Kooktaak aanmaken")}</span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center ${taskOpts.cook ? "bg-brand-green" : "bg-white"}`}>{taskOpts.cook && <Check className="w-4 h-4" strokeWidth={3} />}</span>
            </button>
            {taskOpts.cook && (
              <select data-testid="poll-cook-assignee" value={taskOpts.cookAssignee} onChange={(e) => setTaskOpts({ ...taskOpts, cookAssignee: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2 font-medium outline-none bg-white mt-2 text-sm">
                <option value="">{tr("Niemand → automatisch uitvragen 🔄")}</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            )}
          </div>

          <div className="brutal-sm rounded-xl bg-white px-4 py-3">
            <button data-testid="poll-toggle-shopping" onClick={() => setTaskOpts({ ...taskOpts, shoppingTask: !taskOpts.shoppingTask })} className="w-full flex items-center justify-between font-bold text-sm">
              <span className="flex items-center gap-1.5"><ShoppingCart className="w-4 h-4" strokeWidth={2.5} />{tr("Aparte boodschappentaak")}</span>
              <span className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center ${taskOpts.shoppingTask ? "bg-brand-green" : "bg-white"}`}>{taskOpts.shoppingTask && <Check className="w-4 h-4" strokeWidth={3} />}</span>
            </button>
            {taskOpts.shoppingTask && (
              <select data-testid="poll-shopping-assignee" value={taskOpts.shoppingAssignee} onChange={(e) => setTaskOpts({ ...taskOpts, shoppingAssignee: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2 font-medium outline-none bg-white mt-2 text-sm">
                <option value="">{tr("Niemand → automatisch uitvragen 🔄")}</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
            )}
          </div>

          <div className="flex gap-2">
            <BrutalButton data-testid="poll-create-tasks-btn" variant="green" className="flex-1" onClick={createTasks}>{tr("Uitvoeren")}</BrutalButton>
            <BrutalButton data-testid="poll-no-tasks-btn" variant="white" onClick={() => setTaskPoll(null)}>{tr("Nee, niet nodig")}</BrutalButton>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{editId ? tr("Poll bewerken") : foodMode ? tr("Nieuwe eet-poll") : tr("Nieuwe poll")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Maak of bewerk een poll met opties{foodMode ? " en optioneel gekoppelde recepten" : ""}.</DialogDescription>
          <input data-testid="poll-question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={tr("Waarover wil je stemmen?")} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <div>
            <p className="text-sm font-bold mb-1">{tr("Foto's / bijlagen (meerdere kan, optioneel)")}</p>
            <PhotosInput value={pollPhotos} onChange={setPollPhotos} aiPrompt={question} testid="poll-photos" />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-bold">{foodMode ? tr("Opties (koppel optioneel een recept)") : tr("Opties")}</p>
            <datalist id="poll-opt-suggestions">
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
            {options.map((o, i) => (
              <div key={i} className="space-y-1.5 brutal-sm rounded-xl p-2 bg-white/60">
                <div className="flex flex-wrap gap-1.5">
                  <input data-testid={`poll-option-${i}`} value={o.text} onChange={(e) => setOpt(i, "text", e.target.value)}
                    list="poll-opt-suggestions" placeholder={`${tr("Optie")} ${i + 1}`} className="flex-1 min-w-0 brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
                  {foodMode && (
                  <div className="w-full sm:w-36 sm:shrink-0">
                    <RecipeCombobox recipes={recipes} value={o.recipeId} onChange={(id) => setOpt(i, "recipeId", id)} testid={`poll-option-recipe-${i}`} />
                  </div>
                  )}
                </div>
                {foodMode && (
                  <div>
                    <p className="text-[11px] font-bold text-brand-ink/50 mb-1">{tr("Foto (optioneel)")}</p>
                    <PhotoInput value={o.image} onChange={(u) => setOpt(i, "image", u)} aiPrompt={o.text} testid={`poll-option-photo-${i}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
          <button data-testid="add-poll-option" onClick={() => setOptions([...options, { text: "", recipeId: "", image: "" }])} className="text-sm font-bold underline decoration-2 decoration-brand-violet text-left">{tr("+ Optie toevoegen")}</button>
          <div>
            <label className="text-sm font-bold">{tr("Sluit automatisch op (optioneel)")}</label>
            <input data-testid="poll-closes-at" type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} className="w-full brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none mt-1" />
          </div>
          <div>
            <p className="text-sm font-bold mb-1">{tr("Voor welke groep?")}</p>
            <GroupMultiSelect groups={groups} value={groupIds} onChange={setGroupIds} testid="poll-group" />
          </div>
          <BrutalButton data-testid="create-poll-btn" disabled={savingPoll} onClick={save} className="w-full">{savingPoll ? <><Loader2 className="w-5 h-5 animate-spin" />{tr("Bezig…")}</> : (editId ? tr("Wijzigingen opslaan") : tr("Poll plaatsen"))}</BrutalButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const fmtQty = (n) => {
  if (n === null || n === undefined || n === "") return "";
  const r = Math.round(Number(n) * 100) / 100;
  if (!Number.isFinite(r)) return "";
  return Number.isInteger(r) ? String(r) : String(r).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
};
const ingName = (ing) => (typeof ing === "object" && ing ? ing.name : String(ing));

const emptyForm = () => ({ name: "", description: "", image_url: null, category: "Hoofdgerecht", servings: 4, prep_minutes: "", steps: [{ text: "", image_url: null }], ingredients: [{ qty: "", unit: "", name: "" }] });
const stepText = (s) => (typeof s === "string" ? s : s?.text || "");
const stepImg = (s) => (typeof s === "object" && s ? s.image_url : null);

function Recepten() {
  const confirm = useConfirm();
  const tr = useT();
  const [recipes, setRecipes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [detail, setDetail] = useState(null);
  const [aiDesc, setAiDesc] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => setRecipes((await api.get("/recipes")).data);
  useEffect(() => { load(); }, []);

  const setIng = (i, key, val) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((row, j) => j === i ? { ...row, [key]: val } : row) }));
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { qty: "", unit: "", name: "" }] }));
  const removeIng = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, j) => j !== i) }));
  const setStep = (i, key, val) => setForm((f) => ({ ...f, steps: f.steps.map((s, j) => j === i ? { ...s, [key]: val } : s) }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { text: "", image_url: null }] }));
  const removeStep = (i) => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }));

  const aiGenerate = async () => {
    if (!aiDesc.trim()) return toast.error("Beschrijf kort welk gerecht je wilt");
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/recipe", { description: aiDesc, servings: Number(form.servings) || 4 });
      setForm((f) => ({
        ...f,
        name: data.name || f.name,
        description: data.description || f.description,
        category: RECIPE_CATEGORIES.includes(data.category) ? data.category : "Overig",
        servings: data.servings || f.servings,
        prep_minutes: data.prep_minutes ?? f.prep_minutes,
        ingredients: (data.ingredients || []).length ? data.ingredients.map((r) => ({ qty: r.qty ?? "", unit: r.unit || "", name: r.name || "" })) : f.ingredients,
        steps: (data.steps || []).map((t) => ({ text: typeof t === "string" ? t : (t.text || ""), image_url: null })),
      }));
      toast.success(tr("Recept gegenereerd ✨ — foto wordt gemaakt…"));
      // auto-generate a dish photo in the background
      api.post("/ai/image", { prompt: `Bord met ${data.name || aiDesc}, smakelijk opgemaakt, bovenaanzicht, natuurlijk licht` })
        .then(({ data: img }) => { setForm((f) => (f.image_url ? f : { ...f, image_url: img.url })); toast.success(tr("Foto toegevoegd 🖼️")); })
        .catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || tr("AI-recept mislukt")); }
    finally { setAiBusy(false); }
  };

  const [savingRecipe, setSavingRecipe] = useState(false);
  const create = async () => {
    if (savingRecipe) return;
    if (!form.name.trim()) return toast.error(tr("Naam verplicht"));
    const ingredients = form.ingredients
      .filter((r) => r.name.trim())
      .map((r) => ({ name: r.name.trim(), qty: r.qty === "" ? null : Number(r.qty), unit: r.unit.trim() }));
    setSavingRecipe(true);
    try {
      await api.post("/recipes", {
        name: form.name.trim(), description: form.description.trim(), category: form.category,
        image_url: form.image_url,
        servings: Number(form.servings) || 1, prep_minutes: form.prep_minutes === "" ? null : Number(form.prep_minutes),
        ingredients, steps: form.steps.filter((s) => s.text?.trim()).map((s) => ({ text: s.text.trim(), image_url: s.image_url || null })),
      });
      setOpen(false); setForm(emptyForm()); setAiDesc(""); load(); toast.success(tr("Recept opgeslagen"));
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Opslaan mislukt, probeer opnieuw"));
    } finally {
      setSavingRecipe(false);
    }
  };

  const del = async (id) => { if (!(await confirm({ title: tr("Recept verwijderen?") }))) return; await api.delete(`/recipes/${id}`); if (detail?.id === id) setDetail(null); load(); };
  const [q, setQ] = useState("");
  const filtered = recipes.filter((r) => `${r.name} ${r.category || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4">
      <BrutalButton data-testid="new-recipe-btn" className="w-full" onClick={() => { setForm(emptyForm()); setOpen(true); }}><Plus className="w-5 h-5" strokeWidth={3} />{tr("Nieuw recept")}</BrutalButton>
      {recipes.length > 0 && (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink/40" strokeWidth={2.5} />
          <input data-testid="recipe-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Zoek recept of categorie…")} className="w-full brutal-sm rounded-xl pl-9 pr-4 py-2.5 font-medium outline-none" />
        </div>
      )}
      {recipes.length === 0 && <EmptyState icon={ChefHat} title={tr("Nog geen recepten")} text={tr("Voeg je favoriete gerechten toe.")} />}
      {recipes.length > 0 && filtered.length === 0 && <EmptyState icon={Search} title={tr("Niks gevonden")} text={tr("Geen recept voor deze zoekopdracht.")} />}
      <div className="grid gap-3 sm:grid-cols-2">
        {filtered.map((r) => {
          const avg = avgRating(r);
          return (
          <BrutalCard key={r.id} data-testid={`recipe-${r.id}`} className="cursor-pointer overflow-hidden" onClick={() => setDetail(r)}>
            {recipeImg(r.image_url) && <img src={recipeImg(r.image_url)} alt={r.name} className="w-full h-32 object-cover brutal-sm rounded-xl mb-3" />}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-head font-bold text-lg truncate">{r.name}</h3>
                {r.description && <p className="text-sm text-brand-ink/60 line-clamp-2">{r.description}</p>}
              </div>
              <Tip label={tr("Recept verwijderen")}><button data-testid={`delete-recipe-${r.id}`} onClick={(e) => { e.stopPropagation(); del(r.id); }} className="brutal-sm brutal-press rounded-full p-1.5 bg-white shrink-0"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button></Tip>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {avg > 0 && <Pill className="bg-brand-yellow"><Star className="w-3 h-3 fill-current" strokeWidth={2.5} />{avg.toFixed(1)}</Pill>}
              <Pill className="bg-brand-violet/15"><Users className="w-3 h-3" strokeWidth={2.5} />{r.servings || 1} {tr("pers.")}</Pill>
              {r.prep_minutes ? <Pill className="bg-brand-teal/20"><Clock className="w-3 h-3" strokeWidth={2.5} />{r.prep_minutes} {tr("min")}</Pill> : null}
              {r.category ? <Pill className="bg-brand-pink/25">{tr(r.category)}</Pill> : null}
              <Pill className="bg-white">{(r.ingredients || []).length} {tr("ingrediënten")}</Pill>
            </div>
          </BrutalCard>
          );
        })}
      </div>

      <RecipeDetail recipe={detail} onClose={() => setDetail(null)} onChanged={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{tr("Nieuw recept")}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Maak een recept handmatig of laat AI het genereren.</DialogDescription>

          <div className="brutal-sm rounded-xl bg-brand-violet/10 p-3">
            <p className="text-sm font-bold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-brand-violet" strokeWidth={2.5} />{tr("Genereer met AI")}</p>
            <p className="text-xs text-brand-ink/50 mb-2">{tr("Beschrijf een gerecht, dan vult Wizzy de velden in.")}</p>
            <div className="flex gap-2">
              <input data-testid="ai-recipe-desc" value={aiDesc} onChange={(e) => setAiDesc(e.target.value)} placeholder={tr("Bijv. snelle vegetarische pasta pesto")} className="flex-1 min-w-0 brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
              <BrutalButton data-testid="ai-recipe-btn" size="sm" variant="dark" disabled={aiBusy} onClick={aiGenerate}>
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={2.5} />}{tr("Genereer")}
              </BrutalButton>
            </div>
          </div>

          <input data-testid="recipe-name" placeholder={tr("Naam")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
          <input placeholder={tr("Korte omschrijving")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full brutal-sm rounded-xl px-4 py-2.5 font-medium outline-none" />
          <div>
            <label className="text-xs font-bold text-brand-ink/60">{tr("Foto van het gerecht")}</label>
            <div className="mt-1"><PhotoInput value={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} aiPrompt={form.name || aiDesc} testid="recipe-photo" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs font-bold text-brand-ink/60">{tr("Porties")}</label>
              <input data-testid="recipe-servings" type="number" min="1" value={form.servings} onChange={(e) => setForm({ ...form, servings: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold text-brand-ink/60">{tr("Min.")}</label>
              <input placeholder="30" type="number" min="0" value={form.prep_minutes} onChange={(e) => setForm({ ...form, prep_minutes: e.target.value })} className="w-full brutal-sm rounded-xl px-3 py-2.5 font-medium outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold text-brand-ink/60">{tr("Categorie")}</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="recipe-category" className="w-full brutal-sm rounded-xl px-2 py-2.5 h-auto font-medium bg-white text-sm border-0 mt-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECIPE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{tr(c)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm font-bold">{tr("Ingrediënten")}</label>
            <div className="space-y-2 mt-1">
              {form.ingredients.map((row, i) => (
                <div key={i} className="flex gap-1.5">
                  <input data-testid={`ing-qty-${i}`} placeholder="200" value={row.qty} onChange={(e) => setIng(i, "qty", e.target.value)} className="w-16 brutal-sm rounded-xl px-2 py-2 font-medium outline-none text-center" />
                  <input data-testid={`ing-unit-${i}`} placeholder="g" value={row.unit} onChange={(e) => setIng(i, "unit", e.target.value)} className="w-16 brutal-sm rounded-xl px-2 py-2 font-medium outline-none text-center" />
                  <input data-testid={`ing-name-${i}`} placeholder={tr("Ingrediënt")} value={row.name} onChange={(e) => setIng(i, "name", e.target.value)} className="flex-1 min-w-0 brutal-sm rounded-xl px-3 py-2 font-medium outline-none" />
                  <button onClick={() => removeIng(i)} className="brutal-sm brutal-press rounded-full p-2 bg-white shrink-0"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                </div>
              ))}
            </div>
            <button data-testid="add-ing-btn" onClick={addIng} className="text-sm font-bold underline decoration-2 decoration-brand-violet mt-2">{tr("+ Ingrediënt toevoegen")}</button>
          </div>
          <div className="space-y-3">
            <label className="text-sm font-bold">{tr("Bereiding — stap voor stap")}</label>
            {form.steps.map((s, i) => (
              <div key={i} className="brutal-sm rounded-xl bg-brand-cream p-3 space-y-2" data-testid={`recipe-step-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="font-head font-black text-brand-violet shrink-0">{i + 1}.</span>
                  <input data-testid={`step-text-${i}`} value={s.text} onChange={(e) => setStep(i, "text", e.target.value)} placeholder={`${tr("Stap")} ${i + 1}`} className="flex-1 min-w-0 brutal-sm rounded-xl px-3 py-2 font-medium outline-none bg-white" />
                  {form.steps.length > 1 && <button type="button" data-testid={`remove-step-${i}`} onClick={() => removeStep(i)} className="brutal-sm brutal-press rounded-full p-1.5 bg-white shrink-0"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>}
                </div>
                <PhotoInput value={s.image_url} onChange={(u) => setStep(i, "image_url", u)} aiPrompt={s.text} testid={`step-photo-${i}`} />
              </div>
            ))}
            <button type="button" data-testid="add-step-btn" onClick={addStep} className="text-sm font-bold underline decoration-2 decoration-brand-violet">{tr("+ Stap toevoegen")}</button>
          </div>
          <BrutalButton data-testid="save-recipe-btn" disabled={savingRecipe} onClick={create} className="w-full">{savingRecipe ? <><Loader2 className="w-5 h-5 animate-spin" />{tr("Bezig…")}</> : tr("Opslaan")}</BrutalButton>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RatingStars({ recipeId, ratings: initial, onRated }) {
  const { user } = useAuth();
  const tr = useT();
  const [ratings, setRatings] = useState(initial || {});
  const [hover, setHover] = useState(0);
  const mine = ratings[user.user_id] || 0;
  const vals = Object.values(ratings);
  const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  const rate = async (n) => {
    try { const { data } = await api.post(`/recipes/${recipeId}/rate`, { rating: n }); setRatings(data.ratings); toast.success(tr("Bedankt voor je beoordeling!")); onRated?.(); }
    catch { toast.error(tr("Beoordelen mislukt")); }
  };
  return (
    <div className="brutal-sm rounded-xl bg-brand-yellow/30 p-3 flex items-center justify-between" data-testid="recipe-rating">
      <div>
        <p className="font-bold text-sm">{tr("Jouw beoordeling")}</p>
        {vals.length > 0 && <p className="text-xs text-brand-ink/50">{tr("Gemiddeld")} {avg.toFixed(1)} · {vals.length} {vals.length === 1 ? tr("stem") : tr("stemmen")}</p>}
      </div>
      <div className="flex gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`rate-${n}`} onClick={() => rate(n)} onMouseEnter={() => setHover(n)}>
            <Star className={`w-6 h-6 ${(hover || mine) >= n ? "fill-brand-orange text-brand-orange" : "text-brand-ink/25"}`} strokeWidth={2} />
          </button>
        ))}
      </div>
    </div>
  );
}

function CookMode({ recipe, onClose }) {
  const tr = useT();
  const [i, setI] = useState(0);
  const steps = recipe.steps || [];
  if (steps.length === 0) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-brand-ink text-white flex flex-col p-6" data-testid="cook-mode">
      <div className="flex items-center justify-between">
        <p className="font-head font-black text-xl truncate pr-4">{recipe.name}</p>
        <Tip label={tr("Sluiten")}><button data-testid="cook-close" onClick={onClose} className="brutal-sm brutal-press rounded-full p-2 bg-white/15 shrink-0"><X className="w-5 h-5" strokeWidth={2.5} /></button></Tip>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <p className="font-head font-black text-brand-yellow text-6xl mb-6">{i + 1}<span className="text-white/40 text-3xl">/{steps.length}</span></p>
        {stepImg(steps[i]) && <img src={recipeImg(stepImg(steps[i]))} alt="" className="w-full max-w-md h-48 object-cover brutal-sm rounded-xl mb-4" />}
        <p className="text-2xl font-bold leading-snug max-w-lg" data-testid="cook-step">{stepText(steps[i])}</p>
      </div>
      <div className="flex gap-3">
        <BrutalButton variant="white" className="flex-1" disabled={i === 0} onClick={() => setI(i - 1)} data-testid="cook-prev"><ChevronLeft className="w-5 h-5" strokeWidth={3} />{tr("Vorige")}</BrutalButton>
        {i < steps.length - 1
          ? <BrutalButton variant="green" className="flex-1" onClick={() => setI(i + 1)} data-testid="cook-next">{tr("Volgende")}<ChevronRight className="w-5 h-5" strokeWidth={3} /></BrutalButton>
          : <BrutalButton variant="green" className="flex-1" onClick={onClose} data-testid="cook-done"><Check className="w-5 h-5" strokeWidth={3} />{tr("Klaar!")}</BrutalButton>}
      </div>
    </div>
  );
}

function RecipeDetail({ recipe, onClose, onChanged }) {
  const tr = useT();
  const [servings, setServings] = useState(recipe?.servings || 1);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cook, setCook] = useState(false);

  useEffect(() => {
    if (recipe) {
      setServings(recipe.servings || 1);
      setSelected((recipe.ingredients || []).map((_, i) => i));
    }
  }, [recipe]);

  if (!recipe) return null;
  const base = recipe.servings || 1;
  const factor = base ? servings / base : 1;
  const ings = recipe.ingredients || [];
  const toggle = (i) => setSelected((s) => s.includes(i) ? s.filter((x) => x !== i) : [...s, i]);
  const allOn = selected.length === ings.length;

  const push = async () => {
    if (selected.length === 0) return toast.error("Selecteer minstens één ingrediënt");
    setBusy(true);
    try {
      const { data } = await api.post(`/recipes/${recipe.id}/to-shopping`, { servings: Number(servings), indices: selected });
      toast.success(`${data.added} ingrediënten op de boodschappenlijst!`);
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!recipe} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="recipe-detail" className="brutal border-2 border-brand-ink bg-white rounded-xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-head font-black text-2xl">{recipe.name}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Receptdetails, porties aanpassen en ingrediënten naar de boodschappenlijst sturen.</DialogDescription>
        {recipeImg(recipe.image_url) && <img src={recipeImg(recipe.image_url)} alt={recipe.name} className="w-full h-48 object-cover brutal-sm rounded-xl" data-testid="recipe-detail-image" />}
        {recipe.description && <p className="text-sm text-brand-ink/70">{recipe.description}</p>}
        {recipe.category && <Pill className="bg-brand-pink/25 self-start">{tr(recipe.category)}</Pill>}

        <RatingStars recipeId={recipe.id} ratings={recipe.ratings} onRated={onChanged} />

        <div className="brutal-sm rounded-xl bg-brand-violet/10 p-3 flex items-center justify-between">
          <span className="font-bold flex items-center gap-1.5"><Users className="w-4 h-4" strokeWidth={2.5} />{tr("Porties")}</span>
          <div className="flex items-center gap-3">
            <button data-testid="servings-minus" onClick={() => setServings((s) => Math.max(1, Number(s) - 1))} className="brutal-sm brutal-press rounded-full w-9 h-9 flex items-center justify-center bg-white font-black text-lg">−</button>
            <span data-testid="servings-value" className="font-head font-black text-xl w-8 text-center">{servings}</span>
            <button data-testid="servings-plus" onClick={() => setServings((s) => Number(s) + 1)} className="brutal-sm brutal-press rounded-full w-9 h-9 flex items-center justify-center bg-white font-black text-lg">+</button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <h4 className="font-head font-bold text-lg">{tr("Ingrediënten")}</h4>
          <button data-testid="toggle-all-ings" onClick={() => setSelected(allOn ? [] : ings.map((_, i) => i))} className="text-sm font-bold underline decoration-2 decoration-brand-violet">{allOn ? tr("Deselecteer alles") : tr("Selecteer alles")}</button>
        </div>
        <div className="space-y-1.5">
          {ings.length === 0 && <p className="text-sm text-brand-ink/50">{tr("Geen ingrediënten.")}</p>}
          {ings.map((ing, i) => {
            const isObj = typeof ing === "object" && ing;
            const qty = isObj && ing.qty != null ? fmtQty(ing.qty * factor) : "";
            const unit = isObj ? (ing.unit || "") : "";
            return (
              <button key={i} data-testid={`ing-row-${i}`} onClick={() => toggle(i)}
                className={`w-full brutal-sm rounded-xl px-3 py-2.5 bg-white flex items-center gap-3 text-left ${selected.includes(i) ? "" : "opacity-45"}`}>
                <span className={`w-5 h-5 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${selected.includes(i) ? "bg-brand-green" : "bg-white"}`}>
                  {selected.includes(i) && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                </span>
                {(qty || unit) && <span className="font-bold text-brand-violet w-20 shrink-0">{qty} {unit}</span>}
                <span className="font-medium flex-1">{ingName(ing)}</span>
              </button>
            );
          })}
        </div>

        {recipe.steps?.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-head font-bold text-lg">{tr("Bereiding")}</h4>
              <BrutalButton data-testid="cook-mode-btn" size="sm" variant="dark" onClick={() => setCook(true)}><Play className="w-4 h-4" strokeWidth={2.5} />{tr("Kook-modus")}</BrutalButton>
            </div>
            <ol className="space-y-2">
              {recipe.steps.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="font-head font-black text-brand-violet shrink-0">{i + 1}.</span>
                  <div className="flex-1"><span>{stepText(s)}</span>{stepImg(s) && <img src={recipeImg(stepImg(s))} alt="" className="mt-1.5 w-full max-w-[220px] h-28 object-cover brutal-sm rounded-lg" />}</div>
                </li>
              ))}
            </ol>
          </div>
        )}

        <BrutalButton data-testid="detail-start-poll-btn" variant="dark" className="w-full" onClick={async () => {
          try { await api.post("/polls", { question: `Wie eet mee? — ${recipe.name}`, options: [recipe.name], option_recipe_ids: [recipe.id] }); toast.success(tr('Poll "Wie eet mee?" gestart 🗳️')); }
          catch { toast.error(tr("Poll starten mislukt")); }
        }}>
          <BarChart3 className="w-5 h-5" strokeWidth={2.5} />{tr("Start poll: Wie eet mee?")}
        </BrutalButton>
        <BrutalButton data-testid="detail-to-shopping-btn" variant="green" className="w-full" disabled={busy || selected.length === 0} onClick={push}>
          <ShoppingCart className="w-5 h-5" strokeWidth={2.5} />{selected.length} {tr("naar boodschappen")}
        </BrutalButton>
        {cook && <CookMode recipe={recipe} onClose={() => setCook(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function ShoppingRow({ it, load }) {
  const confirm = useConfirm();
  const tr = useT();
  return (
    <div data-testid={`shopping-${it.id}`} className={`brutal-sm rounded-xl px-4 py-3 bg-white flex items-center gap-3 ${it.checked ? "opacity-50" : ""} ${it.out_of_stock && !it.checked ? "border-2 border-red-400" : ""}`}>
      <button data-testid={`toggle-shopping-${it.id}`} onClick={() => api.post(`/shopping/${it.id}/toggle`).then(load)}
        className={`w-6 h-6 rounded-md border-2 border-brand-ink flex items-center justify-center shrink-0 ${it.checked ? "bg-brand-green" : "bg-white"}`}>
        {it.checked && <Check className="w-4 h-4" strokeWidth={3} />}
      </button>
      <div className="flex-1 min-w-0">
        <span className={`font-bold ${it.checked ? "line-through" : ""}`}>{it.quantity ? `${it.quantity} ` : ""}{it.name}</span>
        {it.out_of_stock && <span className="ml-2 inline-block brutal-sm rounded-full px-2 py-0.5 text-xs font-bold bg-red-500 text-white">{tr("Was op")}</span>}
        {it.recipe_name && <span className="block text-xs text-brand-ink/50 font-semibold truncate">{tr("uit:")} {it.recipe_name}</span>}
      </div>
      {!it.checked && (
        <div className="flex items-center gap-1 shrink-0">
          <button data-testid={`qty-minus-${it.id}`} onClick={() => api.post(`/shopping/${it.id}/quantity`, { quantity: stepQuantity(it.quantity, -1) }).then(load)} className="brutal-sm brutal-press rounded-full w-8 h-8 flex items-center justify-center bg-white"><Minus className="w-4 h-4" strokeWidth={3} /></button>
          <button data-testid={`qty-plus-${it.id}`} onClick={() => api.post(`/shopping/${it.id}/quantity`, { quantity: stepQuantity(it.quantity, 1) }).then(load)} className="brutal-sm brutal-press rounded-full w-8 h-8 flex items-center justify-center bg-white"><Plus className="w-4 h-4" strokeWidth={3} /></button>
        </div>
      )}
      {!it.checked && <button data-testid={`oos-shopping-${it.id}`} onClick={() => api.post(`/shopping/${it.id}/out-of-stock`).then(load)}
        className={`brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold shrink-0 ${it.out_of_stock ? "bg-red-500 text-white" : "bg-white"}`}>{tr("Was op")}</button>}
      <Tip label={tr("Verwijderen")}><button onClick={async () => { if (await confirm({ title: tr("Item verwijderen?") })) api.delete(`/shopping/${it.id}`).then(load); }} className="brutal-sm brutal-press rounded-full p-1.5 bg-white shrink-0"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button></Tip>
    </div>
  );
}

function Boodschappen() {
  const tr = useT();
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [qty, setQty] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [view, setView] = useState("lijst");
  const [wipeOpen, setWipeOpen] = useState(false);

  const load = async () => {
    setItems((await api.get("/shopping")).data);
    setSuggestions((await api.get("/shopping/suggestions")).data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => { e.preventDefault(); if (!name) return; await api.post("/shopping", { name, quantity: qty.trim() || "" }); setName(""); setQty(""); load(); };
  const toTask = async () => { await api.post("/shopping/to-task"); toast.success(tr("Boodschappen doorgezet als taak!")); };
  const clearChecked = async () => { await api.post("/shopping/clear-checked"); load(); toast.success(tr("Archief geleegd")); };
  const wipeList = async (clearOut) => { await api.post("/shopping/clear-list", { clear_out_of_stock: clearOut }); setWipeOpen(false); load(); toast.success(tr("Boodschappenlijst gewist")); };

  const active = items.filter((i) => !i.checked);
  const archived = items.filter((i) => i.checked);
  const byCat = SHOP_CATEGORIES.map((cat) => ({ cat, list: active.filter((i) => (i.category || "Overig") === cat) })).filter((g) => g.list.length);
  const archMap = {};
  archived.forEach((i) => { const d = (i.checked_at || i.created_at || "").slice(0, 10); (archMap[d] = archMap[d] || []).push(i); });
  const archDates = Object.keys(archMap).sort().reverse();
  const fmtD = (d) => new Date(d).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button data-testid="shop-view-lijst" onClick={() => setView("lijst")} className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${view === "lijst" ? "bg-brand-ink text-white" : "bg-white"}`}>{tr("Lijst")} ({active.length})</button>
        <button data-testid="shop-view-archief" onClick={() => setView("archief")} className={`brutal-sm brutal-press rounded-full px-4 py-2 text-sm font-bold ${view === "archief" ? "bg-brand-ink text-white" : "bg-white"}`}>{tr("Archief")} ({archived.length})</button>
      </div>

      {view === "lijst" ? (
        <>
          <form onSubmit={add} className="flex gap-2">
            <input data-testid="shopping-input" list="shopping-suggestions" value={name} onChange={(e) => setName(e.target.value)} placeholder={tr("Voeg item toe…")} className="flex-1 min-w-0 brutal-sm rounded-xl px-4 py-3 font-medium outline-none" />
            <input data-testid="shopping-qty" value={qty} onChange={(e) => setQty(e.target.value)} placeholder={tr("Aantal")} className="w-24 shrink-0 brutal-sm rounded-xl px-3 py-3 font-medium outline-none" />
            <datalist id="shopping-suggestions">{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
            <Tip label={tr("Toevoegen")}><BrutalButton data-testid="add-shopping-btn" type="submit"><Plus className="w-5 h-5" strokeWidth={3} /></BrutalButton></Tip>
          </form>
          {active.length === 0 ? <EmptyState icon={ShoppingCart} title={tr("Lijst is leeg")} text={tr("Voeg items toe of vul aan vanuit een recept.")} /> : (
            <>
              {byCat.map((g) => (
                <div key={g.cat}>
                  <p className="font-head font-bold text-sm text-brand-ink/60 mb-1.5 mt-2">{tr(g.cat)}</p>
                  <div className="space-y-2">{g.list.map((it) => <ShoppingRow key={it.id} it={it} load={load} />)}</div>
                </div>
              ))}
              <div className="flex flex-col sm:flex-row gap-2">
                <BrutalButton data-testid="shopping-to-task-btn" variant="blue" className="flex-1" onClick={toTask}>
                  {tr("Doorzetten als taak")} <ArrowRight className="w-5 h-5" strokeWidth={3} />
                </BrutalButton>
                <BrutalButton data-testid="shopping-wipe-btn" variant="white" className="flex-1" onClick={() => setWipeOpen(true)}>
                  <Trash2 className="w-4 h-4" strokeWidth={2.5} />{tr("Lijst wissen")}
                </BrutalButton>
              </div>
            </>
          )}
        </>
      ) : (
        archived.length === 0 ? <EmptyState icon={Check} title={tr("Archief is leeg")} text={tr("Afgevinkte boodschappen verschijnen hier per datum.")} /> : (
          <>
            {archDates.map((d) => (
              <div key={d}>
                <p className="font-head font-bold text-sm text-brand-ink/60 mb-1.5 mt-2">{fmtD(d)}</p>
                <div className="space-y-2">{archMap[d].map((it) => <ShoppingRow key={it.id} it={it} load={load} />)}</div>
              </div>
            ))}
            <BrutalButton data-testid="clear-checked-btn" variant="white" className="w-full" onClick={clearChecked}><Trash2 className="w-4 h-4" strokeWidth={2.5} />{tr("Archief legen")}</BrutalButton>
          </>
        )
      )}

      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent className="brutal border-2 border-brand-ink bg-white rounded-xl max-w-sm" data-testid="shopping-wipe-dialog">
          <DialogHeader><DialogTitle className="font-head font-black text-xl">{tr("Boodschappenlijst wissen?")}</DialogTitle></DialogHeader>
          {active.filter((i) => i.out_of_stock).length > 0 ? (
            <>
              <DialogDescription className="text-sm text-brand-ink/60">{tr("Er staan")} {active.filter((i) => i.out_of_stock).length} {tr("item(s) met \"Was op\" op de lijst. Wil je die ook wissen, of bewaren voor de volgende keer?")}</DialogDescription>
              <div className="space-y-2">
                <BrutalButton data-testid="wipe-all-btn" variant="danger" className="w-full" onClick={() => wipeList(true)}>{tr("Alles wissen (incl. \"Was op\")")}</BrutalButton>
                <BrutalButton data-testid="wipe-keep-out-btn" variant="green" className="w-full" onClick={() => wipeList(false)}>{tr("Wissen, \"Was op\" bewaren")}</BrutalButton>
                <BrutalButton data-testid="wipe-cancel-btn" variant="white" className="w-full" onClick={() => setWipeOpen(false)}>{tr("Annuleren")}</BrutalButton>
              </div>
            </>
          ) : (
            <>
              <DialogDescription className="text-sm text-brand-ink/60">{tr("Alle")} {active.length} {tr("item(s) op de lijst worden verwijderd.")}</DialogDescription>
              <div className="flex gap-2">
                <BrutalButton data-testid="wipe-cancel-btn" variant="white" className="flex-1" onClick={() => setWipeOpen(false)}>{tr("Annuleren")}</BrutalButton>
                <BrutalButton data-testid="wipe-all-btn" variant="danger" className="flex-1" onClick={() => wipeList(true)}>{tr("Wissen")}</BrutalButton>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
